import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { Achievement } from '../types';

interface AchievementsSectionProps {
  achievements: Achievement[];
}

const AchievementsSection = ({ achievements }: AchievementsSectionProps) => {
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const categories = [
    { id: 'all', name: 'All Milestones', icon: 'Grid3X3' },
    { id: 'award', name: 'Awards', icon: 'Award' },
    { id: 'certification', name: 'Certs', icon: 'Certification' },
    { id: 'recognition', name: 'Recognition', icon: 'Star' },
  ];

  const filteredAchievements = activeCategory === 'all'
    ? achievements
    : achievements.filter(achievement => achievement.category === activeCategory);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'award': return 'Award';
      case 'certification': return 'Certificate';
      case 'recognition': return 'Star';
      default: return 'Trophy';
    }
  };

  return (
    <section className="bg-white border-2 border-[#0C2C39] shadow-[4px_4px_0px_0px_#0C2C39] rounded-[2rem] p-8 sm:p-12 mb-12 relative overflow-hidden">
      {/* Subtle Pattern Grid */}
      <div className="absolute inset-0 z-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(#0C2C39 0.5px, transparent 0.5px)',
          backgroundSize: '24px 24px'
        }} />

      <div className="relative z-10 mb-12">
        <h2 className="text-4xl sm:text-5xl font-black text-[#0C2C39] mb-4 tracking-tighter font-roboto">
          Distinguished <br />
          <span className="text-teal-700/80 italic">Milestones</span>
        </h2>
        <p className="text-[#0C2C39]/50 text-lg font-medium">Professional accomplishments and official certifications</p>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-3 mb-12 relative z-10">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all duration-300 border-2 ${activeCategory === category.id
                ? 'bg-[#0C2C39] text-white border-[#0C2C39] shadow-[2px_2px_0px_0px_rgba(0,0,0,0.1)]'
                : 'bg-white text-[#0C2C39]/60 hover:text-[#0C2C39] border-[#0C2C39]/5 hover:border-[#0C2C39]/20'
              }`}
          >
            <Icon name={category.icon as any} size={14} />
            <span>{category.name}</span>
          </button>
        ))}
      </div>

      {/* Achievements Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10">
        {filteredAchievements.map((achievement) => (
          <div
            key={achievement.id}
            className="group neubrutal-card bg-white p-8 flex flex-col"
          >
            <div className="flex items-start gap-6 mb-8">
              {/* Achievement Badge */}
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-[#FFF9EB] border-2 border-[#0C2C39] rounded-2xl flex items-center justify-center shadow-[2px_2px_0px_0px_#0C2C39] group-hover:shadow-none group-hover:bg-[#0C2C39] group-hover:text-white transition-all duration-300 overflow-hidden p-3 text-[#0C2C39]">
                  {achievement.image ? (
                    <Image
                      src={achievement.image}
                      alt={achievement.imageAlt || achievement.title}
                      className="w-full h-full object-contain filter group-hover:brightness-0 group-hover:invert transition-all duration-300"
                    />
                  ) : (
                    <Icon name={getCategoryIcon(achievement.category) as any} size={32} />
                  )}
                </div>
              </div>

              {/* Header Info */}
              <div className="flex-1">
                <div className="flex flex-col mb-4">
                  <span className="text-[10px] font-black text-teal-700 uppercase tracking-widest mb-1.5">{achievement.category}</span>
                  <h3 className="text-2xl font-black text-[#0C2C39] leading-tight tracking-tight">
                    {achievement.title}
                  </h3>
                </div>

                <div className="flex flex-wrap gap-4 text-[9px] font-black uppercase tracking-widest text-[#0C2C39]/40">
                  <div className="flex items-center space-x-1.5">
                    <Icon name="Building" size={12} />
                    <span>{achievement.issuer}</span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <Icon name="Calendar" size={12} />
                    <span>{achievement.date}</span>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-[#0C2C39]/70 leading-relaxed font-medium mb-8 text-sm flex-grow">
              {achievement.description}
            </p>

            <div className="pt-6 border-t-2 border-[#0C2C39]/5 flex justify-between items-center">
              <span className="text-[10px] font-black text-[#0C2C39]/30 uppercase tracking-[0.2em]">Verified Instance</span>
              <Icon name="ExternalLink" size={14} className="text-[#0C2C39]/20 group-hover:text-teal-700 transition-colors" />
            </div>
          </div>
        ))}
      </div>

      {filteredAchievements.length === 0 && (
        <div className="text-center py-20 relative z-10">
          <Icon name="Search" size={48} className="text-[#0C2C39]/10 mx-auto mb-4" />
          <p className="text-[#0C2C39]/40 font-black uppercase tracking-widest text-xs">No achievements found in this category</p>
        </div>
      )}
    </section>
  );
};

export default AchievementsSection;